import { Component, OnInit, AfterViewInit, TemplateRef, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { fadeInOut } from '../../../services/animations';
import { AlertService, DialogType, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from "../../../services/app-translation.service";
import { Utilities } from "../../../services/utilities";
import { OrganizationViewModel } from '../../model/organizationViewModel';
import { OrganizationService } from '../../api/organization.service';
import { ListViewViewModel } from '../../model/listViewViewModel';
import { Configuration } from '../../index';
import { Router, ActivatedRoute, NavigationExtras } from '@angular/router';
import { Observable } from 'rxjs';
import { Permission } from '../../../models/permission.model';
import { AuthService } from '../../../services/auth.service';

@Component({
    selector: 'app-organization-list',
    templateUrl: './organization-list.component.html',
    styleUrls: ['./organization-list.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OrganizationListComponent implements OnInit, AfterViewInit {
    @ViewChild(MatPaginator, { static: true })
    paginator: MatPaginator;
    @ViewChild(MatSort, { static: true })
    sort: MatSort;

    displayedColumns = ['name', 'enabled', 'organizationNo', 'createdDate', 'updatedDate', 'actions'];
    dataSource: MatTableDataSource<OrganizationViewModel>;
    sourceReport: OrganizationViewModel;
    loadingIndicator: boolean;

    // Filtering
    @Input() currentViewId: string = "all";
    currentSearchText: string;

    availableViews: Observable<ListViewViewModel[]>;

    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private authService: AuthService,
        private organizationService: OrganizationService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private router: Router,
        private route: ActivatedRoute) {

        this.dataSource = new MatTableDataSource<OrganizationViewModel>();
        this.currentSearchText = "";
    }

    ngOnInit() {
        this.availableViews = this.organizationService.getOrganizationViews();

        this.availableViews.subscribe(views => {
            if (this.route.snapshot.fragment && views.find(t => t.viewId === this.route.snapshot.fragment)) {
                this.currentViewId = this.route.snapshot.fragment;
            }

            this.loadData();
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    public applyFilter(filterValue: string) {
        this.dataSource.filter = filterValue;
    }

    private refresh() {
        // Causes the filter to refresh there by updating with recently added data.
        this.applyFilter(this.dataSource.filter);
    }

    public loadData() {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;

        this.organizationService.getOrganizations(this.currentViewId).subscribe(
            orgs => this.onDataLoadSuccessful(orgs),
            error => this.onDataLoadFailed(error)
        );
    }

    private onDataLoadSuccessful(orgs: OrganizationViewModel[]) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;
        this.dataSource.data = orgs;
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"), this.translationService.getTranslation("FetchErrorMessage") + "\r\n" + Utilities.getHttpResponseMessage(error),
            MessageSeverity.error, error);
    }


    public confirmDelete(organization: OrganizationViewModel) {
        let message: string = this.translationService.getTranslation("commands.Delete") +
            " " +
            organization.name +
            "?";
        let action: string = this.translationService.getTranslation("commands.Delete").toUpperCase();

        this.snackBar.open(message, action, { duration: 5000 })
            .onAction().subscribe(() => {
                this.alertService.startLoadingMessage(
                    this.translationService.getTranslation("globalTexts.DeletingMessage"));
                this.loadingIndicator = true;

                this.organizationService.deleteOrganization(organization.organizationId)
                    .subscribe(results => {
                            this.alertService.stopLoadingMessage();
                            this.loadingIndicator = false;
                            this.alertService.showMessage(this.translationService.getTranslation("commands.Delete"),
                                organization.name + " " + this.translationService.getTranslation("globalTexts.deleted"),
                                MessageSeverity.success);
                            this.loadData();
                        },
                        error => {
                            this.alertService.stopLoadingMessage();
                            this.loadingIndicator = false;

                            let errorMessage = this.translationService.getTranslation("globalTexts.Error") +
                                ": " +
                                Utilities.getHttpResponseMessage(error);

                            this.alertService.showStickyMessage(
                                this.translationService.getTranslation("globalTexts.DeleteError"),
                                errorMessage,
                                MessageSeverity.error,
                                error);
                        });
            });
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.changeOrganizations);
    }

    hasPermissionToDelete(): boolean {
        return this.authService.userHasPermission(Permission.deleteOrganizations);
    }

    public viewChanged(selectedOption: MatSelect) {
        var fragment: NavigationExtras =
        {
            fragment: selectedOption.value
            };
        this.router.navigate(['/organizations'], fragment);
        this.loadData();
    }
}
